// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser, getDiscountedPrice, getLocation } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug, stringify } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { LISTING_TYPE_OFFERS_ACCEPTED } from '../../util/types';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  IconLocation,
  IconReviewStar,
  IconHeart,
} from '../../components';

import { sumBy, uniq } from 'lodash';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig, hasDiscountedPrice } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = (
    <span className={classNames(css.priceValue, { [css.withDiscountedPrice]: hasDiscountedPrice })}>
      {formattedPrice}
    </span>
  );
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

function formatAvailableDates(data) {
  const monthName = iso =>
    new Date(iso).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const dayNum = iso => new Date(iso).getUTCDate();
  const isValidIso = s => !!s && !Number.isNaN(Date.parse(s));

  // Collect all valid slots from every month bucket, sorted by start date
  const allSlots = Object.keys(data || {})
    .sort()
    .flatMap(monthKey => {
      const bucket = data[monthKey];
      if (!bucket || !Array.isArray(bucket.timeSlots)) return [];
      return bucket.timeSlots.filter(s => s?.attributes && isValidIso(s.attributes.start));
    })
    .sort((a, b) => new Date(a.attributes.start) - new Date(b.attributes.start));

  if (allSlots.length === 0) return null;

  // Merge contiguous or overlapping ranges (next.start <= current.end means touching/overlap)
  const merged = [];
  let curStart = allSlots[0].attributes.start;
  let curEnd = isValidIso(allSlots[0].attributes.end) ? allSlots[0].attributes.end : curStart;

  for (let i = 1; i < allSlots.length; i++) {
    const slotStart = allSlots[i].attributes.start;
    const slotEnd = isValidIso(allSlots[i].attributes.end) ? allSlots[i].attributes.end : slotStart;
    if (new Date(slotStart) <= new Date(curEnd)) {
      if (new Date(slotEnd) > new Date(curEnd)) curEnd = slotEnd;
    } else {
      merged.push({ start: curStart, end: curEnd });
      curStart = slotStart;
      curEnd = slotEnd;
    }
  }
  merged.push({ start: curStart, end: curEnd });

  // Format only the first (earliest) merged range
  const { start, end } = merged[0];
  const s = new Date(start);
  const e = new Date(end);
  const sameDay =
    s.getUTCFullYear() === e.getUTCFullYear() &&
    s.getUTCMonth() === e.getUTCMonth() &&
    s.getUTCDate() === e.getUTCDate();

  if (sameDay) return `Available ${monthName(start)} ${dayNum(start)}`;

  const sMonth = monthName(start);
  const eMonth = monthName(end);
  const range =
    sMonth === eMonth
      ? `${sMonth} ${dayNum(start)} - ${dayNum(end)}`
      : `${sMonth} ${dayNum(start)} - ${eMonth} ${dayNum(end)}`;

  return `Available ${range}`;
}

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    arrivalDate = null,
    dates = null,
    reviews,
    currentUser,
    onUpdateProfile,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData, monthlyTimeSlots = {} } = currentListing.attributes;
  const availableDates = formatAvailableDates(monthlyTimeSlots);
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const { listingType, cardStyle, bedrooms, bathrooms } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const loc = getLocation(currentListing);
  const listingLocation = !!loc ? (
    <div className={css.location}>
      <IconLocation />
      <span>{loc}</span>
    </div>
  ) : null;

  const datesAvailable = !!availableDates ? (
    <div className={css.datesAvailable}>
      <span>{availableDates}</span>
    </div>
  ) : null;

  const isOffersAccepted = listingType === LISTING_TYPE_OFFERS_ACCEPTED;
  const [discountedPrice, discountPercentage, daysUntilArrival] = isOffersAccepted
    ? [null, null, null]
    : getDiscountedPrice(currentListing, monthlyTimeSlots, arrivalDate);
  const discountedPriceFormatted = discountedPrice && formatMoney(intl, discountedPrice);
  const noOfBedrooms = bedrooms && (
    <div className={css.bedrooms}>
      <FormattedMessage id="ListingCard.bedrooms" values={{ count: bedrooms }} />
    </div>
  );
  const noOfBathrooms = bathrooms && (
    <div className={css.bathrooms}>
      <FormattedMessage id="ListingCard.bathrooms" values={{ count: parseFloat(bathrooms) }} />
    </div>
  );

  const spacer = ' • ';

  const roomsInfo =
    noOfBedrooms && noOfBathrooms ? (
      <div className={css.roomsInfo}>
        {noOfBedrooms}
        {spacer}
        {noOfBathrooms}
      </div>
    ) : (
      <div className={css.roomsInfo}>{noOfBedrooms || noOfBathrooms}</div>
    );

  const favourites = currentUser?.attributes?.profile?.privateData?.favourites || [];
  const isFavourite = favourites.length > 0 && favourites.includes(id);
  const showSaveButton = !!currentUser && !!onUpdateProfile;

  const handleFavouriteClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!showSaveButton) return;
    const updatedFavourites = isFavourite
      ? favourites.filter(uid => uid !== id)
      : favourites.concat(id);
    onUpdateProfile({ privateData: { favourites: uniq(updatedFavourites) } });
  };

  const toMaybe = !!dates ? { search: stringify({ dates }) } : {};
  const listingReviews =
    (reviews && reviews.find(r => r.id === listing.id.uuid)?.reviews?.data?.data) || [];
  const publicReviews = listingReviews.filter(
    review => review.attributes.state === 'public' && review.attributes.deleted === false
  );
  const totalRatingSum = sumBy(publicReviews, function(r) {
    return r?.attributes?.rating || 0;
  });
  const totalRatingCount = publicReviews.length;
  const totalRating = parseFloat(totalRatingSum) / parseFloat(totalRatingCount);

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }} to={{ ...toMaybe }}>
      <div className={css.discountWrapper}>
        {isOffersAccepted ? (
          <div className={css.offersInvited}>
            <FormattedMessage id="ListingCard.offersInvited" />
          </div>
        ) : discountPercentage && discountPercentage > 0 ? (
          <div className={css.discount}>
            <FormattedMessage id="ListingCard.instantDeal" />
          </div>
        ) : null}

        {daysUntilArrival && daysUntilArrival > 0 ? (
          <div
            className={classNames(css.daysUntilArrival, {
              [css.daysUntilArrivalNoSaveButton]: !showSaveButton,
            })}
          >
            <FormattedMessage id="ListingCard.daysUntilArrival" values={{ daysUntilArrival }} />
          </div>
        ) : null}
      </div>
      <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        config={config}
        setActivePropsMaybe={setActivePropsMaybe}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
      />
      {showSaveButton ? (
        <div
          className={css.saveButton}
          onClick={handleFavouriteClick}
          role="button"
          tabIndex={0}
        >
          <IconHeart className={css.saveIcon} filled={isFavourite} />
        </div>
      ) : null}
      <div className={css.info}>
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={css.title}>
              <div>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </div>
              <div className={css.ratingWrapper}>
                {!!totalRating ? (
                  <div className={css.rating}>
                    <span className={css.value}>
                      <IconReviewStar className={css.reviewRatingStar} isFilled={true} />
                      {totalRating.toFixed(1)}
                    </span>
                  </div>
                ) : null}
                {!!totalRatingCount ? (
                  <div className={css.reviewsCount}>
                    <span className={css.value}>({totalRatingCount})</span>
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {listingLocation}
          {datesAvailable}
        </div>
        <div className={css.footer}>
          <div className={css.priceWrapper}>
            {discountedPriceFormatted && (
              <div className={css.discountedPrice}>{discountedPriceFormatted}</div>
            )}
            <PriceMaybe
              price={price}
              hasDiscountedPrice={!!discountedPriceFormatted}
              publicData={publicData}
              config={config}
              intl={intl}
              listingTypeConfig={foundListingTypeConfig}
            />
          </div>
          {roomsInfo}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
